import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { Card, Field, Input, Textarea, Select, PageHeader, PrimaryButton, DangerButton } from "@/components/admin/AdminUI";
import { SeoGenerateButton } from "@/components/admin/SeoGenerateButton";

const EMPTY = {
  name: "", classification: "GREEN", short_summary: "", full_description: "",
  classification_reason: "", sustainability_notes: "", biodegradability_notes: "",
  common_uses: "", better_alternatives: "", certifications: "", source_notes: "",
  seo_title: "", seo_description: "",
  is_public: true,
};

export default function AdminMaterialEdit() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const { data: material } = useQuery({
    queryKey: ["admin-material", id],
    queryFn: async () => (await api.get("/admin/materials")).data.find((m) => m.id === id),
    enabled: !!id,
  });

  // Aliases are stored in their own collection; load + save them alongside the
  // material. New materials don't have aliases until they're saved at least once.
  const { data: aliasRows } = useQuery({
    queryKey: ["admin-material-aliases", id],
    queryFn: async () => (await api.get(`/admin/materials/${id}/aliases`)).data,
    enabled: !!id,
  });
  const [aliases, setAliases] = useState([]);

  useEffect(() => { if (material) setForm({ ...EMPTY, ...material }); }, [material]);
  useEffect(() => { if (aliasRows) setAliases(aliasRows.map((r) => r.alias_name)); }, [aliasRows]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : (e.target ? e.target.value : e) }));

  async function save() {
    setSaving(true);
    try {
      let materialId = id;
      if (isNew) {
        const res = await api.post("/admin/materials", form);
        materialId = res.data.id;
        toast.success("Material created");
      } else {
        await api.patch(`/admin/materials/${id}`, form);
      }
      // Persist aliases against the (possibly newly-created) material id.
      await api.put(`/admin/materials/${materialId}/aliases`, { aliases });
      if (!isNew) toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["admin-materials"] });
      qc.invalidateQueries({ queryKey: ["admin-material", materialId] });
      qc.invalidateQueries({ queryKey: ["admin-material-aliases", materialId] });
      if (isNew) navigate(`/admin/materials/${materialId}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }

  async function remove() {
    if (!window.confirm("Delete this material?")) return;
    await api.delete(`/admin/materials/${id}`);
    toast.success("Deleted");
    navigate("/admin/materials");
  }

  return (
    <div>
      <PageHeader title={isNew ? "New material" : `Edit: ${form.name || "..."}`}
        actions={<>
          <PrimaryButton onClick={save} disabled={saving} data-testid="material-save-button">{saving ? "Saving…" : "Save"}</PrimaryButton>
          {!isNew && <DangerButton onClick={remove}>Delete</DangerButton>}
        </>}
      />
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name"><Input value={form.name} onChange={set("name")} data-testid="material-name-input" /></Field>
            <Field label="Classification">
              <Select value={form.classification} onChange={set("classification")} data-testid="material-classification-select">
                <option value="GREEN">GREEN</option>
                <option value="YELLOW">YELLOW</option>
                <option value="RED">RED</option>
              </Select>
            </Field>
          </div>
          <Field label="Short summary"><Textarea rows={2} value={form.short_summary} onChange={set("short_summary")} /></Field>
          <Field label="Full description"><Textarea rows={5} value={form.full_description} onChange={set("full_description")} /></Field>
          <Field label="Why this classification"><Textarea rows={3} value={form.classification_reason} onChange={set("classification_reason")} /></Field>
          <Field label="Sustainability notes"><Textarea rows={3} value={form.sustainability_notes} onChange={set("sustainability_notes")} /></Field>
          <Field label="Biodegradability notes"><Textarea rows={3} value={form.biodegradability_notes} onChange={set("biodegradability_notes")} /></Field>
          <Field label="Common uses"><Textarea rows={2} value={form.common_uses} onChange={set("common_uses")} /></Field>
          <Field label="Better alternatives"><Textarea rows={2} value={form.better_alternatives} onChange={set("better_alternatives")} /></Field>
          <Field label="Certifications"><Textarea rows={2} value={form.certifications} onChange={set("certifications")} /></Field>
          <Field label="Source notes"><Textarea rows={2} value={form.source_notes} onChange={set("source_notes")} /></Field>

          <AliasesEditor aliases={aliases} onChange={setAliases} />

          <div className="border-t border-[#E9ECEF] pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-wider text-[#6C757D]">SEO</h3>
              <SeoGenerateButton
                disabled={!form.name}
                buildPayload={() => ({
                  kind: "material",
                  name: form.name,
                  classification: form.classification,
                  short_summary: form.short_summary,
                  classification_reason: form.classification_reason,
                  full_description: form.full_description,
                  common_uses: form.common_uses,
                })}
                onResult={({ seo_title, seo_description }) =>
                  setForm((f) => ({ ...f, seo_title, seo_description }))
                }
              />
            </div>
            <Field label="SEO title" hint="Used in browser tab + Google results. Falls back to the material name + classification."><Input value={form.seo_title || ""} onChange={set("seo_title")} maxLength={120} /></Field>
            <Field label="SEO description" hint="155–160 chars ideally. Falls back to the short summary."><Textarea rows={2} value={form.seo_description || ""} onChange={set("seo_description")} maxLength={300} /></Field>
            <p className="text-[11px] text-[#6C757D] mt-1">URL: <code>/materials/{form.slug || "<slug>"}</code> (automatic)</p>
          </div>
        </Card>
        <Card className="space-y-4 h-fit">
          <div className="flex items-center justify-between">
            <span className="text-sm">Public</span>
            <input type="checkbox" checked={!!form.is_public} onChange={set("is_public")} />
          </div>
        </Card>
      </div>
    </div>
  );
}

// Lightweight chips-style editor for the per-material alias list. Aliases are
// normalized to lowercase server-side; we still echo what the user types so
// it doesn't feel jumpy. Submit with Enter, Tab, or comma.
function AliasesEditor({ aliases, onChange }) {
  const [draft, setDraft] = useState("");

  function commit(value) {
    const s = (value || "").trim().toLowerCase();
    if (!s) return;
    if (aliases.includes(s)) {
      setDraft("");
      return;
    }
    onChange([...aliases, s]);
    setDraft("");
  }
  function remove(a) {
    onChange(aliases.filter((x) => x !== a));
  }
  function onKeyDown(e) {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      if (draft.trim()) {
        e.preventDefault();
        commit(draft);
      }
    } else if (e.key === "Backspace" && !draft && aliases.length) {
      onChange(aliases.slice(0, -1));
    }
  }

  return (
    <div className="border-t border-[#E9ECEF] pt-4">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-xs uppercase tracking-wider text-[#6C757D]">Aliases</h3>
        <span className="text-[11px] text-[#6C757D]">{aliases.length} entries</span>
      </div>
      <p className="text-[11px] text-[#6C757D] mb-2">
        Alternative names this material appears under on brand sites (e.g. <em>polyamide → nylon</em>, <em>rayon → viscose</em>). Used by the crawler to auto-map detected materials.
      </p>
      <div
        className="border border-[#E9ECEF] rounded-md p-2 flex flex-wrap gap-1.5 focus-within:border-[#212529]"
        data-testid="material-aliases-editor"
        onClick={(e) => { if (e.target === e.currentTarget) e.currentTarget.querySelector("input")?.focus(); }}
      >
        {aliases.map((a) => (
          <span
            key={a}
            className="inline-flex items-center gap-1 bg-sand/70 border border-[#E9ECEF] rounded-full px-2 py-0.5 text-xs"
            data-testid={`alias-chip-${a}`}
          >
            {a}
            <button
              type="button"
              onClick={() => remove(a)}
              className="text-[#6C757D] hover:text-clay text-base leading-none"
              aria-label={`Remove ${a}`}
              data-testid={`alias-remove-${a}`}
            >×</button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => draft.trim() && commit(draft)}
          placeholder={aliases.length ? "" : "type an alias and press Enter…"}
          className="flex-1 min-w-[160px] bg-transparent border-0 outline-none text-sm px-1 py-0.5"
          data-testid="material-aliases-input"
        />
      </div>
    </div>
  );
}
